/**
 * LOS PARTIDOS QUE FALTAN, DEBAJO DEL CUADRO.
 *
 * QUÉ PREGUNTA CONTESTA. El cuadro contesta «cómo va el torneo»; esto
 * contesta «cuándo y dónde juego yo», que es otra pregunta y que en el cuadro
 * se responde mal: cada partido lleva su fecha en letra chica adentro de su
 * llave, repartidas por un dibujo de dos metros de ancho. Para saber qué se
 * juega el sábado había que recorrer el cuadro entero con el dedo.
 *
 * ORDENADOS POR CUÁNDO SE JUEGAN Y NO POR RONDA. Es una agenda: lo primero
 * de la lista es lo primero que pasa. Los que todavía no tienen fecha caen al
 * final —no se pueden ordenar contra los que sí— y se dicen igual, porque
 * «falta acordar cuándo» es justamente lo que alguien tiene que ver para ir a
 * acordarlo.
 *
 * SOLO LOS QUE SE PUEDEN JUGAR: los dos rivales puestos y sin resultado. Un
 * lugar que espera al ganador de otro partido no es un partido que alguien
 * pueda agendar, y llenar la lista con ellos la vuelve inútil.
 *
 * Y ES DONDE SE ANOTA EL RESULTADO. Es el mismo renglón que dice «Beto vs
 * Ana, el sábado a las 9 en la cancha 2»: cuando terminan de jugar, lo que
 * hay que hacer con ese renglón es escribir el marcador. Mandar a la persona
 * a buscar esa misma llave adentro del cuadro —dos metros de ancho— para
 * anotar lo que ya tiene delante era el paso que hace que nadie anote.
 *
 * QUIÉN PUEDE ANOTAR LO DICE EL SERVIDOR, en `match.canScore`, igual que en
 * el cuadro. Acá no se vuelve a preguntar «¿soy administrador?»: esa regla
 * vive en un solo lugar, y depende del club —cada club decide si los
 * jugadores anotan sus propios partidos— y de quién mira ESTE partido. En la
 * vista pública el campo ni siquiera viaja, así que no hay nada que ofrecer.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { categoryKey, roundSizeOf, searchKey } from '@tennisup/contracts';
import type { DrawableEntry, DrawableMatch, RoundDeadline } from '@tennisup/contracts';
import { categoryName } from '../lib/categoryName';
import { formatDay } from '../lib/formatDay';
import { Paginador, usePaginado } from './Paginado';

/** El `t` de i18next, tal como lo devuelve el hook: escribirlo a mano no compila. */
type Traducir = ReturnType<typeof useTranslation>['t'];

export function NextMatches<M extends DrawableMatch>({
  matches,
  onScore,
  onSchedule,
  debajo,
  deadlines,
}: {
  matches: readonly M[];
  /** Si se ofrece anotar. Cada fila lo decide además por su `canScore`. */
  onScore?: (match: M) => void;
  /**
   * Si se ofrece PONER CUÁNDO Y DÓNDE. Solo quien organiza.
   *
   * Va acá y no solo en el cuadro porque es acá donde se arma el fin de
   * semana: la lista ya está ordenada por fecha, así que quien reparte
   * canchas ve de una qué hay el sábado y qué falta programar. En el cuadro
   * la misma tarea era abrir llave por llave, repartidas por un dibujo de dos
   * metros de ancho.
   *
   * En las tablas es la ÚNICA forma de programar: un todos contra todos no se
   * dibuja como cuadro, así que sin esto no había dónde ponerle cancha a un
   * partido de grupo.
   */
  onSchedule?: (match: M) => void;
  /**
   * Qué dibujar debajo de una fila — el editor de marcador, abierto ahí
   * mismo. Va por acá y no adentro de este componente para que el editor,
   * sus permisos y su guardado sigan viviendo en la pantalla: esto dibuja
   * una agenda y no sabe hablar con el servidor.
   */
  debajo?: (match: M) => ReactNode;
  /**
   * LAS FECHAS LÍMITE DE CADA RONDA, para poder decirlas acá.
   *
   * «Falta acordar cuándo» a secas parecía que no había ninguna fecha, cuando
   * el cuadro —dos metros más arriba— sí decía «se juega antes del 20/09». Una
   * cosa es que el partido no tenga hora acordada y otra que la ronda no tenga
   * plazo: la primera la arreglan los dos jugadores hablando, la segunda es
   * cuándo se les vence.
   *
   * TODAS LAS DEL TORNEO, no las de una categoría: esta lista mezcla cuadros,
   * y cada fila busca la suya.
   */
  deadlines?: readonly RoundDeadline[];
}): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const todos = porJugar(matches);

  /*
   * BUSCAR POR NOMBRE, AUNQUE EL PARTIDO YA TENGA FECHA.
   *
   * En un cuadro de 64 esta lista son treinta y dos renglones en la primera
   * ronda, y quien reparte canchas llega con un nombre en la cabeza: «los de
   * Cañón juegan el sábado». Sin buscador hay que recorrerla entera, y con la
   * paginación encima hay que recorrerla página por página.
   *
   * NO SE LIMITA A LOS QUE NO TIENEN FECHA. Cambiar una fecha ya puesta es la
   * mitad del trabajo —se llueve el sábado y hay que mover doce partidos— y un
   * buscador que solo encuentra lo que falta programar no sirve para eso.
   */
  const buscado = searchKey(query);
  const pendientes =
    buscado === '' ? todos : todos.filter((m) => nombresDe(m).includes(buscado));

  // `reiniciarCon` el texto buscado: al escribir, la página vuelve a la 1.
  // Quedarse en la página 3 de una lista que ahora tiene dos filas es ver un
  // vacío y creer que no hay nadie con ese nombre.
  const pagina = usePaginado(pendientes, query);

  return (
    <section className="next-matches">
      <h2>{t('bracket.upcoming')}</h2>

      {/* El buscador solo cuando hay de dónde buscar: sobre cuatro partidos
          que caben en la pantalla es un campo de más. */}
      {todos.length > POR_PAGINA && (
        <div className="field">
          <label htmlFor="next-search">{t('bracket.findMatch')}</label>
          <input
            id="next-search"
            type="search"
            autoComplete="off"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      )}

      {todos.length === 0 ? (
        <p className="help">{t('bracket.noneLeft')}</p>
      ) : pendientes.length === 0 ? (
        <p className="help">{t('bracket.noneWithThatName')}</p>
      ) : (
        <ul className="next-list">
          {pagina.visibles.map((match) => (
            <li key={match.id} className="next-row">
              <p className="next-who">
                <span>{match.home?.displayName}</span>
                <span className="next-vs">{t('bracket.versus')}</span>
                <span>{match.away?.displayName}</span>
              </p>
              <p className="help next-what">
                {categoryName(t, match)}
              </p>
              {(() => {
                const plazo = plazoDe(matches, deadlines, match);
                return plazo === null ? null : (
                  <p className="help next-deadline">
                    {t('bracket.reportBy', { date: formatDay(plazo) })}
                  </p>
                );
              })()}
              <p className={cuando(match) === null ? 'help next-when next-open' : 'next-when'}>
                {cuando(match) ?? t('bracket.noDateYet')}
                {onSchedule !== undefined && (
                  <button
                    type="button"
                    className="link next-schedule"
                    onClick={() => onSchedule(match)}
                  >
                    {cuando(match) === null ? t('schedule.set') : t('schedule.edit')}
                  </button>
                )}
              </p>

              {/* `=== true` y no a secas: en la vista pública `canScore` NO
                  VIAJA, así que llega `undefined`. Un `undefined` no es
                  «puede», es «no hay quien». */}
              {onScore !== undefined && match.canScore === true && (
                <p className="next-score">
                  <button type="button" className="link" onClick={() => onScore(match)}>
                    {t('score.addOf', {
                      home: match.home?.displayName ?? '',
                      away: match.away?.displayName ?? '',
                    })}
                  </button>
                </p>
              )}

              {debajo?.(match)}
            </li>
          ))}
        </ul>
      )}

      <Paginador
        pagina={pagina.pagina}
        paginas={pagina.paginas}
        irA={pagina.irA}
        de={t('bracket.upcoming')}
      />
    </section>
  );
}

/** Cuántos caben antes de que la lista pida paginarse. */
const POR_PAGINA = 10;

/**
 * LOS NOMBRES DE LOS DOS, listos para buscar.
 *
 * EL APODO Y EL DE PILA, igual que en el buscador del cuadro y por lo mismo:
 * en un torneo hay ocho «Beto» y lo que los distingue es el nombre de abajo.
 * Buscar solo por apodo es ofrecer un buscador que no encuentra a nadie en el
 * único caso en que hace falta.
 *
 * `searchKey` del contrato: sin tildes, sin mayúsculas y sin nada que no sea
 * letra, que es la misma normalización que usa el resto de la aplicación.
 */
function nombresDe(match: DrawableMatch): string {
  const nombre = (entry: DrawableEntry | null): string =>
    entry === null ? '' : `${entry.displayName} ${entry.firstName ?? ''}`;
  return searchKey(`${nombre(match.home)} ${nombre(match.away)}`);
}

/**
 * Cuándo y dónde, en una línea, o `null` si no se sabe nada.
 *
 * LA CANCHA SE AGREGA SI ESTÁ, y no se inventa un «por definir» cuando falta:
 * la mitad de los torneos de club se juegan en la única cancha que hay, y una
 * columna que dice «sin cancha» en todas las filas es ruido. Lo que sí se
 * dice es cuando NO HAY NADA —ni día, ni hora, ni lugar—, porque eso es una
 * tarea pendiente de alguien.
 */
export function cuando(match: DrawableMatch): string | null {
  const partes = [
    match.schedule.date === null ? null : formatDay(match.schedule.date),
    match.schedule.time,
    match.schedule.place,
    match.schedule.court,
  ].filter((parte): parte is string => parte !== null && parte !== '');

  return partes.length === 0 ? null : partes.join(' · ');
}

/**
 * Los que se pueden jugar ya, en el orden en que van a pasar.
 *
 * `date`/`time` son fecha y hora CIVILES —«2026-10-11», «09:00»— y se ordenan
 * como texto a propósito: pegadas dan `2026-10-11 09:00`, que ordena bien sin
 * construir un `Date`. Construirlo sería meter la zona horaria en una cuenta
 * que no la necesita, y ahí es donde un partido del primero de octubre
 * aparece el treinta de septiembre.
 */
export function porJugar<M extends DrawableMatch>(matches: readonly M[]): M[] {
  return matches
    .filter((m) => m.winnerEntryId === null && m.home !== null && m.away !== null)
    .sort((a, b) => clave(a).localeCompare(clave(b)));
}

function clave(match: DrawableMatch): string {
  // La `z` de adelante manda al final a los que no tienen fecha: cualquier
  // año empieza con un dígito, y los dígitos van antes que las letras.
  const fecha =
    match.schedule.date === null
      ? 'z'
      : `${match.schedule.date} ${match.schedule.time ?? '99:99'}`;
  // El desempate por ronda y posición deja el cuadro en su orden natural
  // entre los que comparten fecha, o entre los que no tienen ninguna.
  return `${fecha} ${match.level} ${match.sex} ${String(match.round).padStart(2, '0')} ${String(match.position).padStart(3, '0')}`;
}
/**
 * QUÉ RONDA SE ESTÁ JUGANDO: la más baja que todavía tiene un partido sin
 * resultado. `null` cuando no queda ninguno, que es lo mismo que decir que
 * esa categoría terminó.
 */
export function rondaEnJuego(matches: readonly DrawableMatch[]): number | null {
  const abiertas = matches.filter((m) => m.winnerEntryId === null).map((m) => m.round);
  return abiertas.length === 0 ? null : Math.min(...abiertas);
}

/** «Octavos», «Cuartos», «Semifinal»… para una ronda de un cuadro de `size`. */
export function nombreDeRonda(t: Traducir, size: number, round: number): string {
  const jugadores = size / 2 ** (round - 1);
  if (jugadores === 2) return t('bracket.final');
  if (jugadores === 4) return t('bracket.semi');
  if (jugadores === 8) return t('bracket.quarter');
  return t('bracket.roundOf', { n: jugadores });
}

/**
 * LA FECHA LÍMITE DE LA RONDA DE ESE PARTIDO, o `null` si esa ronda no tiene.
 *
 * LAS DOS FORMAS DE NOMBRAR UNA RONDA SE CRUZAN ACÁ: el partido trae su número
 * de orden y la fecha límite está guardada por tamaño —«la de 32»—, así que hay
 * que convertir. La conversión es `roundSizeOf`, del contrato, que es la misma
 * que usa el servidor: hecha a mano acá, un redondeo distinto mostraría la
 * fecha de otra ronda, que es peor que no mostrar ninguna.
 *
 * Y SE CUENTA CON LOS PARTIDOS DE SU PROPIA CATEGORÍA. Esta lista mezcla
 * cuadros; midiendo contra todos, la ronda más alta sería la del cuadro más
 * grande y un cuartos de un cuadro de 8 se llamaría «ronda de 64».
 */
function plazoDe(
  todos: readonly DrawableMatch[],
  deadlines: readonly RoundDeadline[] | undefined,
  match: DrawableMatch,
): string | null {
  if (deadlines === undefined || deadlines.length === 0) return null;
  const suyos = todos.filter((m) => categoryKey(m) === categoryKey(match));
  const tamaño = roundSizeOf(suyos, match.round);
  return (
    deadlines.find((d) => categoryKey(d) === categoryKey(match) && d.round === tamaño)?.deadline ??
    null
  );
}
